import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  TemplateRef, Output, EventEmitter, DestroyRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  BioKindsEnum,
  BioKindsEnumTranslations,
  LabelColorsEnum,
  LabelColorsTranslations,
  LabelTypesEnum,
  LabelTypesEnumTranslations, UnitTypesEnum
} from '../core/enums';
import { ArticlesVariantsService } from '../core/services';
import { debounceTime, finalize, switchMap, catchError } from 'rxjs/operators';
import { IArticleVariant } from '../core/interfaces';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { merge, Observable, of, Subject } from 'rxjs';
import { ISimpleListItem } from '../core/interfaces/ISimpleListItem';
import * as moment from 'moment';
import { environment } from '../../../../environments/environment';
import { LabelsErrorResolverService } from '../core/services/LabelsErrorResolverService';
import { UnicodeParser } from '../../../core/parsers/UnicodeParser';
import { disableDatesBeforeNow } from '../../../core/configs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getEtikettenartByNumericValue } from '../../etiketten/core/etikettenarten.registry';
import { familyHasField, EtikettField } from '../../etiketten/previews/etiketten-field-schema';
import { LABEL_PREVIEW_DEBOUNCE_MS } from '../../etiketten/previews/label-preview.constants';
import { ILabelPreviewHtml } from '../../../core/interfaces';
import { LabelsService } from '../../../core/services/LabelsService';

export interface ItemNutrition {
  key: string;
  value: string;
}

@Component({
    selector: 'app-edit-label-modal',
    templateUrl: './edit-label-modal.component.html',
    styleUrls: ['./edit-label-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class EditLabelModalComponent implements OnInit {

  @Output() reloadList = new EventEmitter<void>();

  @ViewChild('formElement') formElement: ElementRef<HTMLFormElement>;
  @ViewChild('copyFromOtherVariantRef') copyFromOtherVariantRef: TemplateRef<HTMLFormElement>;

  public isVisible = false;
  public title = 'Bearbeiten';
  public form: FormGroup;
  public loading = false;
  public isArticlesLoading = false;
  public variant: IArticleVariant;
  public articleList: ISimpleListItem[];
  public articleToCopy: ISimpleListItem;
  public units = [
    { label: 'g', value: 'g'},
    { label: 'ml', value: 'ml'},
    { label: 'Stück', value: 'St&#252;ck'},
    { label: 'manuell', value: 'manually'},
  ];
  public readonly disableDatesBeforeNow = disableDatesBeforeNow;

  private searchArticleChange$ = new Subject();

  public readonly toolbarHiddenButtons: string[][] = [
    [
      'undo',
      'redo',
      'italic',
      'underline',
      'strikeThrough',
      'subscript',
      'superscript',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      'justifyFull',
      'indent',
      'outdent',
      'insertUnorderedList',
      'insertOrderedList',
      'heading',
      'fontName'
    ],
    [
      'fontSize',
      'textColor',
      'backgroundColor',
      'customClasses',
      'link',
      'unlink',
      'insertImage',
      'insertVideo',
      'insertHorizontalRule',
      'toggleEditorMode'
    ]
  ];
  public readonly bioKindsEnum = BioKindsEnum;
  public readonly bioKindsEnumTranslations = BioKindsEnumTranslations;
  public readonly labelTypesEnum = LabelTypesEnum;
  public readonly labelTypesEnumTranslations = LabelTypesEnumTranslations;
  public readonly labelColorsEnum = LabelColorsEnum;
  public readonly labelColorsTranslations = LabelColorsTranslations;
  public readonly dateFormat = environment.defaultDateFormat;
  public preview: ILabelPreviewHtml = null;
  public previewLoading = true;
  public schriftgroesse: 'normal' | 'klein' | 'sehr-klein' = 'normal';

  private articleId: number;
  private pendingLabelType?: string;
  private readonly previewTick$ = new Subject<void>();
  private readonly _formFields = {
    ingredients: [''],
    weight: ['', Validators.required],
    unit: [''],
    charge: ['', Validators.required],
    articleNameFirstLine: ['', Validators.required],
    articleNameSecondLine: [''],
    featured: [''],
    fairtradeProduct: [''],
    descriptionFirstLine: ['', Validators.required],
    descriptionSecondLine: [''],
    additionalJapanText: [''],
    mhd: ['', Validators.required],
    labelType: [LabelTypesEnum.XLLabel, Validators.required],
    bioKind: [BioKindsEnum.NONE, Validators.required],
    labelColor: [LabelColorsEnum.Chilli, Validators.required],
    storageLocation: [''],
    ean: [''],
    orderNumber: [''],
    fullName: [''],
    isFairtrade: [false],
    isAllergen: [false],
    hasNutrients: [false],
    hasExpandedIngredients: [false],
    hasIngredientsOnOtherLabel: [false],
    hasSmallerTitle: [false],
    hasSmallerText: [false],
    hasSmallerTextIfManyIngredientsVerticalLabel: [false],
    lineHeight: [null],
    hideMhd: [false]
  };

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _formBuilder: FormBuilder,
    private readonly _articlesVariantsService: ArticlesVariantsService,
    private readonly _errorResolverService: LabelsErrorResolverService,
    private readonly _notifyService: NzNotificationService,
    private readonly _modal: NzModalService,
    private readonly _destroyRef: DestroyRef,
    private readonly _labelsService: LabelsService,
  ) { }

  editNutrientsCache: { [key: string]: { edit: boolean; data: ItemNutrition } } = {};
  nutrients: ItemNutrition[] = [];

  startEdit(index: number): void {
    this.editNutrientsCache[index].edit = true;
  }

  cancelEdit(index: number): void {
    this.editNutrientsCache[index] = {
      data: { ...this.nutrients[index] },
      edit: false
    };
  }

  saveEdit(index: number): void {
    Object.assign(this.nutrients[index], this.editNutrientsCache[index].data);
    this.editNutrientsCache[index].edit = false;
    this.previewTick$.next();
  }

  remove(index: number): void {
    this.nutrients = this.nutrients.filter((item, indexAr) => index !== indexAr);
    this.previewTick$.next();
  }

  updateEditCache(): void {
    this.nutrients.forEach((item, index) => {
      this.editNutrientsCache[index] = {
        edit: false,
        data: { ...item }
      };
    });
    this._cdr.detectChanges();
  }

  ngOnInit(): void {
    this.form = this._formBuilder.group(this._formFields);
    this.form.get('storageLocation').disable();
    this.form.get('ean').disable();
    this.form.get('orderNumber').disable();
    this.form.get('fullName').disable();
    const getArticles = (name: any) => this._articlesVariantsService.getSimpleList(name);

    const articlesOptionList$: Observable<ISimpleListItem[]> = this.searchArticleChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getArticles));
    articlesOptionList$.subscribe(data => {
      this.articleList = data;
      this.isArticlesLoading = false;
      this._cdr.detectChanges();
    });

    const mhdControl = this.form.get('mhd');

    this.form.get('hideMhd').valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(value => {
      if (value) {
        mhdControl.reset();
        mhdControl.disable();
      } else {
        mhdControl.enable();
      }
    });

    merge(this.form.valueChanges, this.previewTick$).pipe(
      debounceTime(LABEL_PREVIEW_DEBOUNCE_MS),
      switchMap(() => this.requestPreviewHtml()),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(preview => {
      if (preview) {
        this.preview = preview;
        this.previewLoading = false;
        this._cdr.markForCheck();
      }
    });
  }

  public hasField(field: EtikettField): boolean {
    const family = getEtikettenartByNumericValue(this.form?.get('labelType')?.value)?.family ?? 'F1';
    return familyHasField(family, field);
  }

  public onSchriftgroesseChange(value: 'normal' | 'klein' | 'sehr-klein'): void {
    this.schriftgroesse = value;
    this.form.patchValue({
      hasSmallerTitle: value !== 'normal',
      hasSmallerText: value !== 'normal',
      hasSmallerTextIfManyIngredientsVerticalLabel: value === 'sehr-klein',
    });
  }

  public showModal(articleId: number, options?: { labelType?: string }): void {
    this.pendingLabelType = options?.labelType;
    this.isVisible = true;
    this.preview = null;
    this.previewLoading = true;
    if (articleId) {
      this.articleId = articleId;
      this.loadLabel(articleId);
    }
    this._cdr.detectChanges();
  }

  public handleCancel(): void {
    this.pendingLabelType = undefined;
    this.isVisible = false;
    this._cdr.detectChanges();
  }

  public submit(): void {
    for (const key in this.form.controls) {
      if (this.form.controls.hasOwnProperty(key)) {
        this.form.controls[key].markAsDirty();
        this.form.controls[key].updateValueAndValidity();
      }
    }

    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    this.variant.labelInfo = { ...this.form.value, weight: this.prepareWeightAndUnit() };
    this.variant.labelInfo.mhd = this.variant.labelInfo.mhd
      ? moment(this.variant.labelInfo.mhd).toDate().toDateString()
      : null;

    if (this.variant.labelInfo.ingredients) {
      this.variant.labelInfo.ingredients = this.variant.labelInfo.ingredients.replace(
        /(<\/?(?:b|p)[^>]*>)|<[^>]+>/ig,
        '$1'
      ).replace(
        / style="[^"]*"/g,
        ''
      ).replace(
        /<p>/g,
        ''
      ).replace(
        /<\/p>/g,
        '<br>'
      ).replace(
        /<br>$/,
        ''
      ).replace(
        /^(\s+<br( \/)?>)*|(<br( \/)?>\s)*$/gm,
        ''
      ).replace(
        /\u00A0/g,
        '&#160;'
      ).replace(
        /,&#160;/g,
        ', '
      ).replace(
        /\u00A0/g,
        '&#160;'
      ).trim();

      const lineHeight = this.form.get('lineHeight').value;

      delete this.variant.labelInfo.lineHeight;

      this.variant.labelInfo.ingredients = `
        <p style="line-height: ${ lineHeight ? lineHeight + 'mm' : 'initial' };">${ UnicodeParser.replaceGlyphs(this.variant.labelInfo.ingredients) }</p>
      `.replaceAll(
        '\n',
        ''
      ).replace(
        '<p><br><br>',
        '<p><br>'
      ).trim();
    }

    this.variant.labelInfo.nutritionalValues = this.nutrients;
    this._articlesVariantsService.update(this.variant, this.variant.id).pipe(
      finalize(() => {
        this.loading = false;
        this._cdr.detectChanges();
      })
    ).subscribe((variant) => {
      this.loadVariant(variant);
      this.reloadList.emit();
      this._notifyService.success('Erfolg', 'Etikett bearbeitet.');
    }, this.parseError.bind(this));
  }

  public copyFromOtherVariant() {
    this._modal.create({
      nzTitle: 'von anderem Etikett übernehmen',
      nzContent: this.copyFromOtherVariantRef,
      nzMaskClosable: false,
      nzClosable: true,
      nzOnOk: (event) => {
        if (!this.articleToCopy) {
          this._notifyService.error('', 'Zunächst müssen Sie eine Variante auswählen');
          return;
        }
        this.loading = true;

        this._articlesVariantsService.getById(this.articleToCopy.id).pipe(
          finalize(() => {
            this.loading = false;
            this._cdr.detectChanges();
          })
        ).subscribe(value =>  {
           this.form.patchValue(value.labelInfo);
           this.nutrients = value.labelInfo.nutritionalValues;
           this.updateEditCache();
           this._cdr.detectChanges();
        }, this.parseError.bind(this));
      }
    });
  }

  public regenerateLabel() {
    this.loading = true;
    this._articlesVariantsService.generateLabel(this.variant.id).pipe(
      finalize(() => {
        this.loading = false;
        this._cdr.detectChanges();
      })
    ).subscribe(() => {
       this.loadLabel(this.articleId);
    }, this.parseError.bind(this));
  }

  public addEmptyNutrition() {
    this.nutrients = [
      ...this.nutrients,
      {
        key: '',
        value: '',
      }
    ];

    this.updateEditCache();
    this.previewTick$.next();
    this.nutrients.forEach((value, index) => (this.saveEdit(index)));
    this.startEdit(this.nutrients.length - 1);
    this._cdr.detectChanges();
  }

  onArticleForCopySearch(value: string) {
    this.isArticlesLoading = true;
    this.searchArticleChange$.next(value);
  }

  public onChangeUnit(): void {
    const unit = this.form.value['unit'];

    if (unit !== UnitTypesEnum.manually && this.form.value['weight']) {
      const weight = this.form.value['weight'].split(' ');
      if (weight.length > 1) {
        this.form.patchValue({ weight: weight[0] });
      }
    }
  }

  private prepareWeightAndUnit(): string {
    let unit = this.form.get('unit').value;
    unit = unit === UnitTypesEnum.manually ? '' : unit;
    let weight = this.form.get('weight').value;

    if (unit) {
      weight = `${weight.split(' ')[0]} <span data-unit>${unit}</span>`;
    } else {
      weight = weight.split(' ');
      weight = `${weight[0]} <span data-unit>${UnicodeParser.replaceGlyphs(weight[1])}</span>`;
    }

    return weight;
  }

  private loadLabel(articleId: number): void {
    this.form.reset();
    this.form.patchValue({ unit: 'g'});
    this.preview = null;
    this.previewLoading = true;
    this.nutrients = [];
    this._cdr.detectChanges();
    this.loading = true;
    this._articlesVariantsService.getById(articleId.toString()).pipe(
      finalize(() => {
        this.loading = false;
        this._cdr.detectChanges();
      })
    ).subscribe(res => {
      this.loadVariant(res);
    }, this.parseError.bind(this));
  }

  private parseError(data: any): void {
    this._notifyService.error('', this._errorResolverService.getMessage((data && data.InternalCode) ? data.InternalCode : ''));
  }

  private parseUnitAndWeight(): void {
    if (!this.variant.labelInfo.weight) {
      this.form.patchValue({ unit: 'g' });
      return;
    }

    const weightAndUnit = this.variant.labelInfo.weight
    .replace('<span data-unit>', '')
    .replace('</span>', '')
    .split(' ');

    if (weightAndUnit.length <= 1) {
      this.form.patchValue({ weight: weightAndUnit[0] });
      return;
    }

    weightAndUnit[1] = UnicodeParser.replaceUnicode(weightAndUnit[1]);
    const unitLabels = [];
    this.units.forEach(unit => {
      unitLabels.push(unit.label);
    });
    if (unitLabels.includes(weightAndUnit[1])) {
      this.form.patchValue({ unit: UnicodeParser.replaceGlyphs(weightAndUnit[1]) });
    } else {
      weightAndUnit.length > 1 ? this.form.patchValue({ unit: 'manually'}) : this.form.patchValue({ unit: 'g'});
    }
    unitLabels.includes(weightAndUnit[1]) ?
      this.form.patchValue({ weight: weightAndUnit[0] }) :
      this.form.patchValue({ weight: weightAndUnit.join(' ')});
  }

  private loadVariant(res: IArticleVariant): void {
    this.variant = res;
    this.form.patchValue(res.labelInfo);
    if (this.pendingLabelType) {
      this.form.patchValue({ labelType: this.pendingLabelType });
      this.pendingLabelType = undefined;
    }

    this.parseUnitAndWeight();

    this.form.get('storageLocation').patchValue(res.storageLocation);
    this.form.get('ean').patchValue(res.ean);
    this.form.get('orderNumber').patchValue(res.orderNumber);
    this.form.get('fullName').patchValue(`${ res.name } ${ res.variantName }`);
    this.nutrients = this.variant.labelInfo.nutritionalValues ?? [];
    this.syncSchriftgroesseFromForm();
    this.updateEditCache();
    this.previewTick$.next();
  }

  private syncSchriftgroesseFromForm(): void {
    if (this.form.get('hasSmallerTextIfManyIngredientsVerticalLabel').value) {
      this.schriftgroesse = 'sehr-klein';
    } else if (this.form.get('hasSmallerTitle').value || this.form.get('hasSmallerText').value) {
      this.schriftgroesse = 'klein';
    } else {
      this.schriftgroesse = 'normal';
    }
  }

  private requestPreviewHtml(): Observable<ILabelPreviewHtml | null> {
    const labelType = this.form?.getRawValue()?.labelType;
    if (!this.form || !this.isVisible || !labelType) {
      return of(this.preview);
    }
    return this._labelsService.previewHtml(this.buildPreviewRequest()).pipe(
      catchError(() => of(this.preview))
    );
  }

  private buildPreviewRequest(): Record<string, unknown> {
    const v = this.form.getRawValue();
    return {
      ingredients: v.ingredients || '',
      hasExpandedIngredients: !!v.hasExpandedIngredients,
      allIngredientsAreBio: !!v.bioKind && v.bioKind !== BioKindsEnum.NONE,
      nutritionalValues: this.nutrients ?? [],
      articleNameFirstLine: v.articleNameFirstLine || '',
      articleNameSecondLine: v.articleNameSecondLine || '',
      featured: v.featured || '',
      mhd: v.mhd ? moment(v.mhd).toISOString() : null,
      descriptionFirstLine: v.descriptionFirstLine || '',
      descriptionSecondLine: v.descriptionSecondLine || '',
      charge: v.charge || '',
      weight: this.previewWeightHtml(v),
      isFairtrade: !!v.isFairtrade,
      fairtradeProduct: v.fairtradeProduct || '',
      isAllergen: !!v.isAllergen,
      bioKind: v.bioKind,
      labelColor: v.labelColor,
      labelType: v.labelType,
      hasNutrients: !!v.hasNutrients,
      hasIngredientsOnOtherLabel: !!v.hasIngredientsOnOtherLabel,
      hasSmallerTitle: !!v.hasSmallerTitle,
      hasSmallerText: !!v.hasSmallerText,
      hasSmallerTextIfManyIngredientsVerticalLabel: !!v.hasSmallerTextIfManyIngredientsVerticalLabel,
      additionalJapanText: v.additionalJapanText || '',
      hideMhd: !!v.hideMhd,
      ean: v.ean || '',
      storageLocation: v.storageLocation || '',
    };
  }

  private previewWeightHtml(value: { weight?: string; unit?: string }): string {
    const unit = value.unit === UnitTypesEnum.manually ? '' : (value.unit || '');
    const raw = (value.weight || '').toString();
    if (!raw) {
      return '';
    }
    if (unit) {
      return `${raw.split(' ')[0]} <span data-unit>${unit}</span>`;
    }
    const parts = raw.split(' ');
    if (parts.length > 1) {
      return `${parts[0]} <span data-unit>${UnicodeParser.replaceGlyphs(parts[1])}</span>`;
    }
    return raw;
  }
}
